import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL, GOOGLE_MAPS_API_KEY } from '../../utils/constants';
import { FaMapMarkerAlt, FaSave, FaHome, FaBuilding, FaCity, FaGlobe, FaMapPin, FaSearch, FaEdit, FaCheckCircle, FaFlag } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './UpdateLocationScreen.css';

const UpdateLocationScreen = () => {
  const { user, setUser } = useUser();
  const [location, setLocation] = useState({
    name: user?.location?.name || '',
    city: user?.location?.city || '',
    state: user?.location?.state || '',
    country: user?.location?.country || '',
    pincode: user?.location?.pincode || '',
    addressLine1: user?.location?.addressLine1 || '',
    addressLine2: user?.location?.addressLine2 || '',
    landmark: user?.location?.landmark || '',
    latitude: user?.location?.latitude || '',
    longitude: user?.location?.longitude || '',
    mapAddress: user?.location?.mapAddress || '',
  });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  const handleInputChange = (field, value) => {
    setLocation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveLocation = async () => {
    try {
      setSaving(true);
      const accessToken = localStorage.getItem('ACCESS_TOKEN');
      
      if (!accessToken) {
        throw new Error('Access token not found. Please log in again.');
      }

      const payload = {
        location: {
          ...location,
          latitude: location.latitude || '',
          longitude: location.longitude || '',
          mapAddress: location.mapAddress || '',
        },
      };

      const response = await fetch(`${API_BASE_URL}api/doctor/profile/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setUser(prev => ({ ...prev, location: location }));
        toast.success('Location updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('Error updating location:', errorData);
        toast.error('Failed to update location. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An unexpected error occurred while updating your location.');
    } finally {
      setSaving(false);
    }
  };

  const searchPlaces = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    if (isGoogleLoaded && window.google && window.google.maps && window.google.maps.places) {
      try {
        const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
        const request = {
          input: query,
          includedRegionCodes: ['IN']
        };
        
        const suggestions = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        if (suggestions && suggestions.suggestions) {
          setSearchResults(suggestions.suggestions);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);
        setSearchResults([]);
      }
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      searchPlaces(query);
    }, 300);
    setSearchTimeout(timeout);
  };

  const handlePlaceSelect = async (suggestion) => {
    if (!isGoogleLoaded) return;
    
    try {
      const { Place } = await window.google.maps.importLibrary('places');
      const place = new Place({
        id: suggestion.placePrediction.placeId,
        requestedLanguage: 'en'
      });
      
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'addressComponents', 'location']
      });
      
      const addressComponents = place.addressComponents;
      const name = place.displayName;
      const formattedAddress = place.formattedAddress;
      const { lat, lng } = place.location;

      let addressLine1 = '';
      let addressLine2 = '';
      let city = '';
      let state = '';
      let country = '';
      let pincode = '';

      if (addressComponents) {
        addressComponents.forEach(component => {
          const types = component.types;
          
          if (types.includes('street_number')) {
            addressLine1 += component.longText + ' ';
          } else if (types.includes('route')) {
            addressLine1 += component.longText;
          } else if (types.includes('subpremise')) {
            addressLine2 = component.longText + ', ' + addressLine2;
          } else if (types.includes('premise')) {
            addressLine2 = component.longText + ', ' + addressLine2;
          } else if (types.includes('locality')) {
            city = component.longText;
          } else if (types.includes('administrative_area_level_1')) {
            state = component.longText;
          } else if (types.includes('country')) {
            country = component.longText;
          } else if (types.includes('postal_code')) {
            pincode = component.longText;
          }
        });
      }

      setLocation({
        name: name || '',
        city,
        state,
        country,
        pincode,
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim(),
        landmark: location.landmark,
        latitude: lat,
        longitude: lng,
        mapAddress: formattedAddress
      });

      setSearchQuery('');
      setSearchResults([]);
      setShowSearchModal(false);
      toast.success('Location selected successfully!');
    } catch (error) {
      console.error('Error fetching place details:', error);
      toast.error('Failed to fetch location details');
    }
  };

  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => setIsGoogleLoaded(true);
      document.head.appendChild(script);
    } else {
      setIsGoogleLoaded(true);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, []);

  const locationFields = [
    { key: 'name', label: 'Clinic/Hospital Name', icon: <FaBuilding />, placeholder: 'Enter clinic or hospital name' },
    { key: 'city', label: 'City', icon: <FaCity />, placeholder: 'Enter your city' },
    { key: 'state', label: 'State', icon: <FaMapMarkerAlt />, placeholder: 'Enter your state' },
    { key: 'country', label: 'Country', icon: <FaGlobe />, placeholder: 'Enter your country' },
    { key: 'pincode', label: 'Pincode', icon: <FaMapPin />, placeholder: 'Enter pincode', type: 'number' },
    { key: 'addressLine1', label: 'Address Line 1', icon: <FaHome />, placeholder: 'Street address, building number' },
    { key: 'addressLine2', label: 'Address Line 2', icon: <FaBuilding />, placeholder: 'Apartment, suite, floor' },
    { key: 'landmark', label: 'Landmark', icon: <FaFlag />, placeholder: 'Nearby landmark (optional)' },
  ];

  return (
    <DashboardLayout title="Update Location" showBackButton={true}>
      <div className="update-location-container">
        <div className="location-header">
          <div className="header-content">
            <FaMapMarkerAlt className="header-icon" />
            <div>
              <h2>Professional Location</h2>
              <p>Set your professional location to help patients find you easily</p>
            </div>
          </div>
        </div>

        {/* Quick Search Card */}
        <div className="search-card">
          <div className="card-header">
            <FaSearch className="card-icon" />
            <h3>Quick Search</h3>
          </div>
          <p>Search and select your professional location from Google Maps</p>
          <button 
            className="search-button"
            onClick={() => setShowSearchModal(true)}
            disabled={!isGoogleLoaded}
          >
            <FaMapMarkerAlt />
            {isGoogleLoaded ? 'Search Location' : 'Loading Maps...'}
          </button>
        </div>

        {/* Manual Entry Card */}
        <div className="form-card">
          <div className="card-header">
            <FaEdit className="card-icon" />
            <h3>Manual Entry</h3>
          </div>
          <p>Or enter your location details manually</p>
          
          <div className="form-grid">
            {locationFields.map((field) => (
              <div key={field.key} className="form-group">
                <label className="form-label">
                  {field.icon}
                  <span>{field.label}</span>
                </label>
                <input
                  type={field.type || 'text'}
                  value={location[field.key]}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="form-input"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <button 
          className="save-button"
          onClick={handleSaveLocation}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="button-spinner"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <FaCheckCircle />
              <span>Save Location</span>
            </>
          )}
        </button>

        {/* Search Modal */}
        {showSearchModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-title">
                  <FaSearch />
                  <h3>Search Location</h3>
                </div>
                <button 
                  className="modal-close"
                  onClick={() => {
                    setShowSearchModal(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <p>Search for your professional location using Google Maps</p>
                <div className="search-container">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    placeholder="Type your location here..."
                    className="search-input"
                    autoFocus
                  />
                  
                  {searchResults.length > 0 && (
                    <div className="search-results">
                      {searchResults.map((suggestion, index) => {
                        const mainText = suggestion.placePrediction?.structuredFormat?.mainText?.text || 
                                       suggestion.placePrediction?.text?.text || 
                                       String(suggestion.placePrediction?.text || 'Unknown location');
                        const secondaryText = suggestion.placePrediction?.structuredFormat?.secondaryText?.text || 
                                             String(suggestion.placePrediction?.structuredFormat?.secondaryText || '');
                        
                        return (
                          <div
                            key={suggestion.placePrediction?.placeId || index}
                            className="search-result-item"
                            onClick={() => handlePlaceSelect(suggestion)}
                          >
                            <FaMapMarkerAlt className="result-icon" />
                            <div className="result-text">
                              <div className="result-main">{mainText}</div>
                              <div className="result-secondary">{secondaryText}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UpdateLocationScreen;